import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '../../ui/Button/Button';

export interface RestaurantCardProps {
  id: string;
  name: string;
  cuisine: string;
  logo?: string;
  images: string[];
  description?: string;
  onViewMenu?: () => void;
  className?: string;
}

export const RestaurantCard: React.FC<RestaurantCardProps> = ({
  name,
  cuisine,
  logo,
  images,
  description,
  onViewMenu,
  className = '',
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`relative rounded-2xl overflow-hidden ${className}`}>
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={images[0]}
          alt={name}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[rgba(26,26,26,0.9)] via-[rgba(26,26,26,0.5)] to-transparent" />

      {/* Content */}
      <div className="relative p-6 min-h-[200px] flex flex-col">
        {/* Top Row - Logo, Name, Expand Button */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            {/* Logo */}
            {logo ? (
              <img
                src={logo}
                alt={`${name} logo`}
                className="w-14 h-14 rounded-full object-cover bg-white"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-[#D4A054] flex items-center justify-center">
                <span className="text-white font-display text-xl">
                  {name.charAt(0)}
                </span>
              </div>
            )}

            {/* Name & Cuisine */}
            <div>
              <h3 className="text-white font-display text-2xl mb-1">
                {name}
              </h3>
              <p className="text-white/80 text-sm">
                {cuisine}
              </p>
            </div>
          </div>

          {/* Expand Button */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-[#D4A054] hover:bg-[#FDF6E9] transition-colors duration-200 shadow-lg"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Bottom Row - View Menu Button & Image Gallery */}
        <div className="mt-auto pt-8 flex items-end justify-between">
          <Button
            variant="secondary"
            size="sm"
            onClick={onViewMenu}
          >
            View Menu
          </Button>

          {/* Image Gallery Thumbnails */}
          <div className="flex gap-2">
            {images.slice(0, 3).map((image, index) => (
              <div
                key={index}
                className="w-16 h-16 rounded-lg overflow-hidden border-2 border-white/30"
              >
                <img
                  src={image}
                  alt={`${name} ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Expanded Content */}
        <AnimatePresence>
          {expanded && description && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="pt-4 mt-4 border-t border-white/20">
                <p className="text-white/90 text-sm leading-relaxed">
                  {description}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default RestaurantCard;
